import { FindhelpCategory, UniteUsService } from '@src/api.types';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import React from 'react';
import {
  capitalizeSentenceFirstLetters,
  prettyTextToKebabCase,
} from '@repo/utils';
import { IconBrandProps } from '@uhg-abyss/web/ui/IconBrand/IconBrand';
import { CboCustomModal } from '../CboCustomReferral/CboCustomModal';
import { useState } from 'react';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { CboReferralSubmissionResults } from '../CboReferralSubmissionResults/CboReferralSubmissionResults';
import { getFeatureFlagsFromStorage } from '@src/utills/storage';

export type CboCategoriesPanelProps = {
  categoryData: CategoryData[];
  onClickCategoryButton: (clicked: CategoryData) => void;
  vendor?: 'findhelp' | 'Unite Us';
};

// NOTE - Findhelp categories & Uniteus categories can both trivially
//        be coerced into this type, making this panel generic.
export type CategoryData = {
  id: string;
  label: string;
  children?: CategoryData[];
};

export function coerceFindhelpCategories(
  cats: FindhelpCategory[]
): CategoryData[] {
  return cats.map((cat) => {
    return {
      id: String(cat.id),
      label: cat.label,
      children: cat.children
        ? coerceFindhelpCategories(cat.children)
        : undefined,
    };
  });
}

export function coerceUniteUsCategories(
  cats: UniteUsService[]
): CategoryData[] {
  return cats.map((cat) => {
    return {
      id: cat.id,
      label: cat.label,
      children: cat.children
        ? coerceUniteUsCategories(cat.children)
        : undefined,
    };
  });
}

export const CboCategoriesPanel = ({
  categoryData,
  vendor,
  onClickCategoryButton,
}: CboCategoriesPanelProps) => {
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [referResp, setReferResponse] = useState(null); // Add state for referral response
  const featureFlags = getFeatureFlagsFromStorage();
  const onClose = () => {
    setIsCustomModalOpen(false); // Close the custom modal
  };

  categoryData
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((category) => category.label);

  return (
    <React.Fragment>
      <Flex direction="row" alignContent="stretch">
        {categoryData.map((category: CategoryData) => {
          const kebabCaseLabel = prettyTextToKebabCase(category.label);

          return (
            <div
              key={`${category.id}-card`}
              style={{
                marginRight: '8px',
                borderTop: 'solid 2px #F3F3F3',
                borderBottom: 'solid 2px #F3F3F3',
                borderLeft: 'solid 1px #F3F3F3',
                borderRight: 'solid 1px #F3F3F3',
                marginTop: '0px !important',
                marginBottom: '8px',
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              <button
                onClick={() => onClickCategoryButton(category)}
                data-cy={`show-${kebabCaseLabel}-filters-btn`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '4px',
                  background: '#FBFBFB',
                  width: '128px',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  lineHeight: 'normal',
                }}
              >
                <IconBrand
                  size="28px"
                  key={`${category.label}-icon`}
                  icon={getIconName(category.label)}
                  brand="uhc"
                />
                <Text fontSize="sm" fontWeight="bold" color="$core.color.brand.60" data-cy={`show-${kebabCaseLabel}-filters-label`} style={{ textAlign: 'center' }}>
                  {capitalizeSentenceFirstLetters(category.label)}
                </Text>
              </button>
            </div>
          );
        })}

        {/* Custom button */}
        {vendor === 'findhelp' && featureFlags?.cc_custom_cbo?.enabled && (
          <div
            key="custom-button-card"
            style={{
              marginRight: '8px',
              borderTop: 'solid 2px #D2EBFC',
              borderBottom: 'solid 2px #D2EBFC',
              borderLeft: 'solid 1px #D2EBFC',
              borderRight: 'solid 1px #D2EBFC',
              marginTop: '0px !important',
              marginBottom: '8px',
              cursor: 'pointer',
              display: 'flex',
            }}
          >
            <Tooltip content="Access non-Community Connector CBOs to refer and support members with services outside the standard referral system.">
              <button
                onClick={() => {
                  setIsCustomModalOpen(true);
                  setIsResultsModalOpen(false);
                }}
                data-cy="custom-ref-button"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '4px',
                  background: '#EEF8FB',
                  width: '128px',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  lineHeight: 'normal',
                }}
              >
                <IconSymbol
                  icon="add"
                  size="28px"
                  color="$core.color.brand.100"
                />
                <Text fontSize="sm" fontWeight="bold" color="$core.color.brand.60" data-cy="custom-ref-button-label">
                  Custom
                </Text>
              </button>
            </Tooltip>
          </div>
        )}
      </Flex>

      {/* Render the custom modal */}
      {isCustomModalOpen && featureFlags?.cc_custom_cbo?.enabled && (
        <CboCustomModal
          isOpen={isCustomModalOpen}
          setReferResponse={setReferResponse}
          onClose={onClose}
          setIsResultsModalOpen={setIsResultsModalOpen}
        />
      )}
      {/* Render the submission results modal */}
      {isResultsModalOpen && featureFlags?.cc_custom_cbo?.enabled && (
        <CboReferralSubmissionResults
          referResp={referResp}
          setReferResp={setReferResponse}
          fromBulkRefer={undefined}
          setBulkReferResp={undefined}
        />
      )}
    </React.Fragment>
  );
};

function getIconName(categoryLabel: string): IconBrandProps['icon'] {
  switch (categoryLabel) {
    case 'Benefits Navigation':
      return 'additional_benefits';

    case 'Care':
      return 'care';

    case 'Education':
      return 'medal';

    case 'Emergency':
      return 'alert';

    case 'Employment':
      return 'clipboard';

    case 'Entrepreneurship':
      return 'handshake';

    case 'Food':
    case 'Food Assistance':
      return 'healthy_food';

    case 'Goods':
    case 'Clothing and Household Goods':
    case 'Clothing & Household Goods':
      return 'store';

    case 'Health':
      return 'heart_health';

    case 'Housing':
    case 'Housing and Shelter':
    case 'Housing & Shelter':
      return 'home';

    case 'Individual and Family Support Services':
    case 'Individual & Family Support':
      return 'ribbon';

    case 'Legal':
      return 'document_multiple';

    case 'Mental/Behavioral Health':
      return 'mental_health';

    case 'Money':
    case 'Income Support':
    case 'Money Management':
      return 'cost';

    case 'Payments':
      return 'cost';

    case 'Physical Health':
      return 'health';

    case 'Social Enrichment':
      return 'high_five';

    case 'Spiritual Enrichment':
      return 'wellness';

    case 'Sports and Recreation':
    case 'Sports & Recreation':
      return 'run';

    case 'Substance Use':
      return 'pill';

    case 'Transit':
    case 'Transportation':
      return 'bus';

    case 'Utilities':
      return 'light_bulb';

    case 'Wellness':
      return 'happy_smile';

    case 'Work':
      return 'suitcase';
    default: {
      console.warn(`No icon found for category: ${categoryLabel}`);
      return '' as any;
    }
  }
}

